import React from "react";
import {
  FieldValues,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  UseFormGetValues,
  UseFormTrigger,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

export type FormProps<
  S extends z.ZodTypeAny,
  T extends FieldValues = z.infer<S>
> = Omit<JSX.IntrinsicElements["form"], "onSubmit"> & {
  onSubmit?: SubmitHandler<T>;
  onSubmitError?: SubmitErrorHandler<T>;
  schema?: S;
  children:
    | React.ReactNode
    | ((props: {
        trigger: UseFormTrigger<T>;
        getValues: UseFormGetValues<T>;
      }) => React.ReactNode);
};

/**
 * Handles setting up the react-hook-form context and connecting to the form component.
 */
export const Form = <
  S extends z.ZodTypeAny,
  T extends z.infer<S> = z.infer<S>
>({
  onSubmit,
  onSubmitError,
  schema,
  children,
  ...props
}: FormProps<S, T>) => {
  const methods = useForm<T>({
    resolver: schema ? zodResolver(schema) : undefined,
  });
  const { handleSubmit, trigger, getValues } = methods;

  return (
    <FormProvider<T> {...methods}>
      <form
        {...props}
        onSubmit={handleSubmit(onSubmit || noop, onSubmitError || noop)}
      >
        {typeof children === "function"
          ? children({ trigger, getValues })
          : children}
      </form>
    </FormProvider>
  );
};

Form.displayName = "Form";

export default Form;

const noop = () => undefined;
